/// <amd-module name="Build/Extensions/GitHubEnterprise/PersonalAccessTokenPanel" />
import * as React from "react";

import * as Resources from "Build/Extensions/GitHubEnterprise/Resources";

import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";

import { Checkbox } from "VSSUI/Checkbox";
import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";
import { Panel } from "VSSUI/Panel";
import { TextField } from "VSSUI/TextField";

export interface PersonalAccessTokenPanelProps {
    createEndpoint: (serverUrl: string, token: string, acceptUntrustedSslCerts: boolean) => Promise<void>;
    onDismiss: () => void;
}

export interface PersonalAccessTokenPanelState {
    serverUrl: string;
    token: string;
    acceptUntrustedSslCerts: boolean;
    isCreating: boolean;
    errorMessage: string;
}

export class PersonalAccessTokenPanel extends VssComponent<PersonalAccessTokenPanelProps, PersonalAccessTokenPanelState, IVssComponentContext> {
    constructor(props: PersonalAccessTokenPanelProps, context: IVssComponentContext) {
        super(props, context);

        this.state = {
            serverUrl: "",
            token: "",
            acceptUntrustedSslCerts: false,
            isCreating: false,
            errorMessage: ""
        }
    }

    public render(): JSX.Element {
        return (
            <Panel
                className={"pat-panel"}
                onDismiss={this.props.onDismiss}
                titleProps={{ text: Resources.NewConnectionPanelTitle }}
                defaultActiveElement=".auth-info-item input"
                footerButtonProps={[
                    {
                        text: Resources.Cancel,
                        onClick: this.props.onDismiss
                    },
                    {
                        text: this.state.isCreating ? Resources.Creating : Resources.Create,
                        primary: true,
                        disabled: !this._canAuthorize() || this.state.isCreating,
                        onClick: this._onAuthorizeClick
                    }
                ]}
            >
                <div className="content flex-column flex-grow">
                    {
                        this.state.errorMessage &&
                        <MessageBar
                            severity={MessageBarSeverity.Error}>
                            {this.state.errorMessage}
                        </MessageBar>
                    }
                    <TextField
                        className={"fontWeightSemiBold"}
                        label={Resources.ServerUrlLabel}
                        placeholder={Resources.ServerUrlPlaceholder}
                        value={this.state.serverUrl}
                        onChange={(e, value) => this.setState({ serverUrl: value })} />

                    <Checkbox
                        label={Resources.AcceptUntrustedSSL}
                        checked={this.state.acceptUntrustedSslCerts}
                        onChange={(e, value) => this.setState({ acceptUntrustedSslCerts: value })} />

                    <div className="auth-info-item">
                        <TextField
                            className={"fontWeightSemiBold"}
                            label={Resources.PersonalAccessTokenLabel}
                            value={this.state.token}
                            inputType="password"
                            onChange={(e, value) => this.setState({ token: value })} />
                        <div className="description secondary-text">
                            <div>{Resources.PersonalAccessTokenDescription}</div>
                            <div>{Resources.PersonalAccessTokenScopes}</div>
                        </div>
                    </div>
                </div>
            </Panel>
        );
    }

    private _onAuthorizeClick = (): void => {
        this.setState({
            isCreating: true,
            errorMessage: ""
        });
        this.props.createEndpoint(
            this.state.serverUrl,
            this.state.token,
            this.state.acceptUntrustedSslCerts)
            .catch((e) => {
                let errorMessage = "";
                if (e) {
                    if (e.message) {
                        errorMessage = e.message;
                    } else if (typeof e === "string") {
                        errorMessage = e;
                    }
                }
                this.setState({
                    isCreating: false,
                    errorMessage: errorMessage
                });
            });
    }

    private _canAuthorize = (): boolean => {
        return !!(
            this.state.serverUrl &&
            this.state.serverUrl.length > 0 &&
            this.state.token &&
            this.state.token.length > 0);
    }
}